import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock3, FileText, CheckCircle2, XCircle, Clock, AlertTriangle, Download, Trash2, RefreshCw, Undo2 } from "lucide-react";
import { FileDropZone } from "@/components/ui/FileDropZone";
// Download used for "Generuj PDF" button
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useTimesheets, useCreateTimesheet, useDeleteTimesheet, useMissingTimesheet } from "../hooks/useTimesheets";
import { TimesheetPdfModal } from "../components/TimesheetPdfModal";
import { timesheetsApi } from "../api/timesheets";
import { TIMESHEET_STATUS_COLORS, MONTH_NAMES } from "../types";
import { downloadBlob } from "@/utils/download";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import { useAuthStore } from "@/store/auth";
import { showsMyB2BInvoices } from "@/utils/contractViews";
import {
  TIMESHEET_MISSING_BADGE_CHIP,
  TIMESHEET_MISSING_BADGE_LABEL,
} from "../badges";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

// Build selectable periods given an inclusive range of month offsets relative
// to the current month (negative = future, positive = past).
function buildPeriods(fromOffset: number, toOffset: number) {
  const periods: { year: number; month: number; label: string }[] = [];
  for (let i = fromOffset; i >= toOffset; i--) {
    let m = CURRENT_MONTH - i;
    let y = CURRENT_YEAR;
    if (m <= 0) { m += 12; y -= 1; }
    else if (m > 12) { m -= 12; y += 1; }
    periods.push({ year: y, month: m, label: `${MONTH_NAMES[m]} ${y}` });
  }
  return periods;
}

// Submission modal: last 6 months + current.
const PERIODS = buildPeriods(5, 0);
// Blank hours card: previous, current and next month.
const BLANK_PERIODS = buildPeriods(1, -1);

function StatusIcon({ status }: { status: string }) {
  if (status === "approved") return <CheckCircle2 size={14} className="text-green-600" />;
  if (status === "rejected") return <XCircle size={14} className="text-red-500" />;
  if (status === "withdrawal_requested") return <RefreshCw size={14} className="text-orange-500" />;
  if (status === "withdrawn") return <Undo2 size={14} className="text-gray-400" />;
  return <Clock size={14} className="text-yellow-500" />;
}

/* Legend for the dot next to "Moje karty godzin" in the sidebar (same wording — see
   ../badges.ts; same "filed" rule as the badge and the lock). Always shown, so the
   red badge seen in the menu can be looked up even when it is already gone here. */
function TimesheetBadgeLegend({
  missing,
  periodLabel,
  deadlineAt,
  required,
  showB2BNote,
}: {
  missing: boolean;
  periodLabel: string;
  deadlineAt: string | null;
  required: boolean | null;
  showB2BNote: boolean;
}) {
  return (
    <div className="mb-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
        Co oznacza licznik przy „Moje karty godzin" w menu
      </p>
      <div className="flex items-center gap-3 text-sm">
        <span
          className={`min-w-[1.75rem] rounded-full px-1.5 py-0.5 text-center font-mono text-[10px] leading-none ${TIMESHEET_MISSING_BADGE_CHIP} ${missing ? "" : "opacity-40"}`}
        >
          {missing ? 1 : 0}
        </span>
        <span className={missing ? "text-gray-700 dark:text-gray-200" : "text-gray-400"}>
          {TIMESHEET_MISSING_BADGE_LABEL} ({periodLabel})
          {required === false
            ? " — za ten miesiąc karta nie jest od Ciebie wymagana"
            : deadlineAt && <> — termin: {formatDateTime(deadlineAt)}</>}
        </span>
      </div>
      <ul className="mt-3 list-disc space-y-1 pl-4 text-xs text-gray-500 dark:text-gray-400">
        <li>
          Za złożoną uznajemy kartę oczekującą na zatwierdzenie, zatwierdzoną lub w trakcie
          wycofania. Kartę odrzuconą albo wycofaną trzeba złożyć ponownie.
        </li>
        <li>
          Po terminie aplikacja ogranicza się do Urlopów i Harmonogramu, dopóki karta nie
          zostanie złożona.
        </li>
        {showB2BNote && (
          <li>
            Miesiące na umowie B2B rozliczasz fakturą — za nie karta nie jest wymagana. W
            miesiącu zmiany umowy wystarczy jeden dokument: karta godzin albo faktura B2B.
          </li>
        )}
        <li>
          Licznik w menu odświeża się co kilka minut — po złożeniu karty może jeszcze
          chwilę pokazywać starą liczbę.
        </li>
      </ul>
    </div>
  );
}

export function TimesheetsPage() {
  const { data: timesheets = [], isLoading } = useTimesheets();
  const createTimesheet = useCreateTimesheet();
  const deleteTimesheet = useDeleteTimesheet();
  const {
    isMissing,
    year: prevYear,
    month: prevMonth,
    deadlineAt,
    required: prevRequired,
  } = useMissingTimesheet();
  const user = useAuthStore((s) => s.user);

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(CURRENT_MONTH === 1 ? CURRENT_YEAR - 1 : CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH === 1 ? 12 : CURRENT_MONTH - 1);
  const [note, setNote] = useState("");
  // Primary (UoP/UZ-baseline) — always required.
  const [primaryFile, setPrimaryFile] = useState<File | null>(null);
  // Per-secondary-contract optional file: { contractId → File }.
  const [secondaryFiles, setSecondaryFiles] = useState<Record<string, File | null>>({});
  // Slot being resubmitted over an approved card: "primary" | contractId | null.
  // Only ONE card per period may be swapped at a time (backend enforces too).
  const [resubmitSlot, setResubmitSlot] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Active secondary UZ contracts + primary contract info (kind + company) for
  // the currently-selected upload period.
  const { data: contractsPayload } = useQuery({
    queryKey: ["timesheets-my-secondary-with-primary", year, month],
    queryFn: () => timesheetsApi.mySecondaryContractsWithPrimary(year, month),
    enabled: open,
  });
  const secondaryContracts = contractsPayload?.secondaries ?? [];
  const primaryInfo = contractsPayload?.primary;
  const existingTimesheets = contractsPayload?.existing ?? [];

  // Identify what is already submitted (and not rejected) for the selected period.
  // Rejected items count as "missing" — user can resubmit after deleting from the list.
  // A slot mid-replacement has TWO entries: old (withdrawal_requested) + new (pending).
  const primaryEntries = existingTimesheets.filter((e) => e.secondary_contract === null);
  const primaryOccupying = primaryEntries.filter((e) => e.status !== "rejected");
  const canSubmitPrimary = primaryOccupying.length === 0;
  const primaryInFlight = primaryEntries.some((e) => e.status === "withdrawal_requested");
  const existingSecondaryIds = new Set(
    existingTimesheets
      .filter((e) => e.secondary_contract !== null && e.status !== "rejected")
      .map((e) => e.secondary_contract as string),
  );
  // Any replacement already awaiting the director's decision blocks starting
  // another one in this period (one swap at a time — mirrors the backend rule).
  const anyInFlight = existingTimesheets.some((e) => e.status === "withdrawal_requested");
  // The primary slot can be swapped only when it holds nothing but an approved card.
  const canResubmitPrimary =
    !anyInFlight
    && primaryOccupying.length > 0
    && primaryOccupying.every((e) => e.status === "approved");

  const PRIMARY_LABELS: Record<string, string> = {
    uop: "Umowa o pracę",
    uz: "Umowa zlecenie",
    b2b: "Kontrakt B2B",
  };
  const primaryKindLabel = PRIMARY_LABELS[primaryInfo?.contract_type ?? "uop"] ?? "Umowa";
  const primaryCompany =
    primaryInfo?.company_short_code ?? primaryInfo?.company_name ?? null;

  const [pdfOpen, setPdfOpen] = useState(false);
  const [blankModalOpen, setBlankModalOpen] = useState(false);
  const [blankYear, setBlankYear] = useState(CURRENT_YEAR);
  const [blankMonth, setBlankMonth] = useState(CURRENT_MONTH);
  const [blankDownloading, setBlankDownloading] = useState(false);

  const downloadBlankPdf = async () => {
    setBlankDownloading(true);
    try {
      const blob = await timesheetsApi.exportBlankPdf({ year: blankYear, month: blankMonth });
      downloadBlob(blob, `karta_godzin_pusta_${blankYear}_${String(blankMonth).padStart(2, "0")}.pdf`);
      setBlankModalOpen(false);
    } catch {
      // silent — user can retry
    } finally {
      setBlankDownloading(false);
    }
  };

  const reset = () => {
    setNote("");
    setPrimaryFile(null);
    setSecondaryFiles({});
    setResubmitSlot(null);
    setErrors({});
    setSubmitting(false);
  };

  const openForPrevious = () => {
    reset();
    setYear(prevYear);
    setMonth(prevMonth);
    setOpen(true);
  };

  const submit = async () => {
    const e: Record<string, string> = {};
    // Primary file is required ONLY if there's no existing non-rejected primary
    // — or when the user chose to swap the approved primary card.
    if ((canSubmitPrimary || resubmitSlot === "primary") && !primaryFile) {
      e.primary = canSubmitPrimary
        ? "Skan karty podstawowej jest wymagany."
        : "Wybierz skan nowej karty do wymiany.";
    }
    if (resubmitSlot && resubmitSlot !== "primary" && !secondaryFiles[resubmitSlot]) {
      e.submit = "Wybierz skan nowej karty do wymiany.";
    }
    // Need at least ONE file to submit (otherwise — nothing to do).
    const anySecondaryToUpload = Object.entries(secondaryFiles).some(
      ([cid, f]) => f && (!existingSecondaryIds.has(cid) || resubmitSlot === cid),
    );
    if (!canSubmitPrimary && !resubmitSlot && !anySecondaryToUpload) {
      e.submit = "Nie wybrano żadnego pliku do złożenia.";
    }
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setErrors({});
    setSubmitting(true);

    const uploadOne = async (scan: File, secondaryContractId: string | null, resubmit = false) =>
      new Promise<void>((resolve, reject) => {
        const fd = new FormData();
        fd.append("year", String(year));
        fd.append("month", String(month));
        fd.append("note", note);
        fd.append("scan", scan);
        if (secondaryContractId) fd.append("secondary_contract", secondaryContractId);
        if (resubmit) fd.append("resubmit", "1");
        createTimesheet.mutate(fd, {
          onSuccess: () => resolve(),
          onError: (err) => reject(err),
        });
      });

    try {
      // 1. Primary — if not already submitted (rejected counts as missing) or being swapped.
      if ((canSubmitPrimary || resubmitSlot === "primary") && primaryFile) {
        await uploadOne(primaryFile, null, resubmitSlot === "primary");
      }
      // 2. Each secondary UZ in sequence — skip inactive in period AND skip ones
      //    already submitted (unless that slot is the one being swapped).
      const activeIds = new Set(
        secondaryContracts.filter((c) => c.is_active_in_period).map((c) => c.id),
      );
      for (const [contractId, file] of Object.entries(secondaryFiles)) {
        const isSwap = resubmitSlot === contractId;
        if (
          file
          && activeIds.has(contractId)
          && (isSwap || !existingSecondaryIds.has(contractId))
        ) {
          await uploadOne(file, contractId, isSwap);
        }
      }
      setOpen(false);
      reset();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
        ?.response?.data?.errors?.detail;
      setErrors({ submit: detail ?? "Błąd podczas składania kart. Spróbuj ponownie." });
      setSubmitting(false);
    }
  };

  // Resubmit a rejected timesheet: delete the rejected row, then re-open the
  // submission modal scoped to its period. Modal will detect (via existing_timesheets
  // refresh) that the slot is again "missing" and show the input.
  const resubmitRejected = (t: { id: string; year: number; month: number }) => {
    deleteTimesheet.mutate(t.id, {
      onSuccess: () => {
        reset();
        setYear(t.year);
        setMonth(t.month);
        setOpen(true);
      },
    });
  };

  // Sort: newest first, primary contract above secondary for the same month.
  const sorted = [...timesheets].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    if (b.month !== a.month) return b.month - a.month;
    // primary (null) before secondary (non-null)
    if (!a.secondary_contract && b.secondary_contract) return -1;
    if (a.secondary_contract && !b.secondary_contract) return 1;
    return 0;
  });

  const pendingCount = timesheets.filter((t) => t.status === "pending").length;

  return (
    <div className="max-w-3xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Moje karty godzin</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Składaj karty godzin do 2. dnia roboczego następnego miesiąca, do godz. 12:00.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="secondary" onClick={() => setPdfOpen(true)}>
            <Download size={15} className="mr-1.5" /> Generuj PDF
          </Button>
          <Button
            variant="secondary"
            onClick={() => setBlankModalOpen(true)}
          >
            <Download size={15} className="mr-1.5" /> Pusta karta
          </Button>
          <Button onClick={() => { reset(); setOpen(true); }}>
            <Clock3 size={15} className="mr-1.5" /> Złóż kartę
          </Button>
        </div>
      </div>

      <TimesheetBadgeLegend
        missing={isMissing}
        periodLabel={`${MONTH_NAMES[prevMonth]} ${prevYear}`}
        deadlineAt={deadlineAt}
        required={prevRequired}
        showB2BNote={showsMyB2BInvoices(user)}
      />

      {isMissing && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span>
              Nie złożono karty godzin za{" "}
              <span className="font-semibold">{MONTH_NAMES[prevMonth]} {prevYear}</span>.
              {deadlineAt && <> Termin: {formatDateTime(deadlineAt)}.</>}
            </span>
          </div>
          <Button onClick={openForPrevious}>
            Złóż teraz
          </Button>
        </div>
      )}

      {pendingCount > 0 && (
        <div className="mb-4 flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-4 py-2.5 text-sm text-yellow-800 dark:text-yellow-300">
          <Clock size={15} />
          {pendingCount === 1
            ? "1 karta czeka na zatwierdzenie przez dyrektora."
            : `${pendingCount} karty czekają na zatwierdzenie przez dyrektora.`}
        </div>
      )}

      {isLoading ? (
        <PageLoader />
      ) : sorted.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center">
          <Clock3 size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak złożonych kart godzin.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((ts) => (
            <div
              key={ts.id}
              className="bg-white dark:bg-gray-800 px-4 py-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      {ts.period_display}
                    </span>
                    <Badge color={ts.secondary_contract ? "purple" : "blue"}>
                      {ts.secondary_contract
                        ? `UZ (dodatkowa od ${formatDate(ts.secondary_contract_detail?.effective_from, "")})`
                        : "Umowa podstawowa"}
                    </Badge>
                    <Badge color={TIMESHEET_STATUS_COLORS[ts.status]}>
                      <StatusIcon status={ts.status} />
                      <span className="ml-1">{ts.status_display}</span>
                    </Badge>
                    {ts.replaces && ts.status === "pending" && (
                      <Badge color="orange">wymiana</Badge>
                    )}
                  </div>
                  {ts.note && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{ts.note}</p>
                  )}
                  {ts.review_comment && (
                    <p className={`text-xs mt-1 font-medium ${ts.status === "rejected" ? "text-red-600" : "text-green-700 dark:text-green-400"}`}>
                      Komentarz: {ts.review_comment}
                    </p>
                  )}
                  {ts.status === "withdrawal_requested" && (
                    <p className="text-xs mt-1 text-orange-600 dark:text-orange-400">
                      Zgłoszono do wycofania — czeka na akceptację dyrektora.
                    </p>
                  )}
                  {ts.withdrawal_comment && (
                    <p className="text-xs mt-1 font-medium text-orange-600 dark:text-orange-400">
                      Wycofanie: {ts.withdrawal_comment}
                    </p>
                  )}
                  {ts.reviewed_by_name && ts.reviewed_at && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Decyzja: {ts.reviewed_by_name} · {new Date(ts.reviewed_at).toLocaleDateString("pl-PL")}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {ts.scan_managed_file_detail?.url && (
                    <a
                      href={ts.scan_managed_file_detail.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                    >
                      <FileText size={12} /> Skan
                    </a>
                  )}
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(ts.created_at).toLocaleDateString("pl-PL")}
                  </span>
                  {ts.status === "rejected" && (
                    <button
                      onClick={() => resubmitRejected({ id: ts.id, year: ts.year, month: ts.month })}
                      className="text-xs text-[var(--color-primary)] hover:underline"
                      title="Usuń odrzuconą i złóż ponownie"
                    >
                      Złóż ponownie
                    </button>
                  )}
                  {(ts.status === "pending" || ts.status === "rejected") && (
                    <button
                      onClick={() => setDeleteId(ts.id)}
                      className="text-gray-300 hover:text-red-500 transition-colors"
                      title={ts.replaces && ts.status === "pending"
                        ? "Anuluj wymianę — przywróci poprzednią kartę"
                        : "Usuń"}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit modal */}
      <Modal open={open} onClose={() => setOpen(false)} title="Złóż kartę godzin">
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Okres <span className="text-red-500">*</span>
            </label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={`${year}-${month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                setYear(y);
                setMonth(m);
                setResubmitSlot(null);
              }}
            >
              {PERIODS.map((p) => (
                <option key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uwagi <span className="text-gray-400 text-xs">(opcjonalne)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Dodatkowe informacje..."
            />
          </div>

          {/* Karta podstawowa — input pojawia się tylko gdy nie ma jeszcze
              złożonej (lub poprzednia została odrzucona). Inaczej badge "już złożona". */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {primaryKindLabel}
              {primaryCompany ? ` — ${primaryCompany}` : ""}{" "}
              {canSubmitPrimary && <span className="text-red-500">*</span>}
            </label>
            {canSubmitPrimary ? (
              <>
                <FileDropZone
                  files={primaryFile ? [primaryFile] : []}
                  onFilesChange={(fs) => setPrimaryFile(fs[0] ?? null)}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                {errors.primary && <p className="text-xs text-red-500 mt-1">{errors.primary}</p>}
              </>
            ) : (
              <>
                <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  <span>
                    Karta {primaryKindLabel.toLowerCase()} za {month.toString().padStart(2, "0")}/{year}{" "}
                    jest już złożona{" "}
                    {primaryInFlight ? (
                      <Badge color="orange">wymiana w toku</Badge>
                    ) : (
                      <>
                        {primaryOccupying.some((e) => e.status === "approved") && <Badge color="green">zatwierdzona</Badge>}
                        {primaryOccupying.some((e) => e.status === "pending") && <Badge color="yellow">oczekuje</Badge>}
                      </>
                    )}
                    .
                  </span>
                  {primaryInFlight && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1.5">
                      Poprzednia karta oczekuje na akceptację wycofania przez dyrektora,
                      a nowa — na zatwierdzenie.
                    </p>
                  )}
                  {canResubmitPrimary && (
                    <label className="flex items-center gap-2 mt-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 dark:border-gray-600"
                        checked={resubmitSlot === "primary"}
                        disabled={resubmitSlot !== null && resubmitSlot !== "primary"}
                        onChange={(ev) => {
                          setResubmitSlot(ev.target.checked ? "primary" : null);
                          setPrimaryFile(null);
                        }}
                      />
                      <span>Złóż ponownie — wymień zatwierdzoną kartę</span>
                    </label>
                  )}
                </div>
                {resubmitSlot === "primary" && (
                  <div className="mt-2">
                    <p className="text-xs text-amber-700 dark:text-amber-400 mb-1.5">
                      Obecna karta zostanie zgłoszona do wycofania. Dyrektor osobno
                      zaakceptuje wycofanie starej karty i osobno zatwierdzi nową.
                    </p>
                    <FileDropZone
                      files={primaryFile ? [primaryFile] : []}
                      onFilesChange={(fs) => setPrimaryFile(fs[0] ?? null)}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                    {errors.primary && <p className="text-xs text-red-500 mt-1">{errors.primary}</p>}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Po jednym dropzonie na każdą dodatkową UZ pracownika — opcjonalne.
              Dropzone jest disabled gdy umowa nie obowiązuje w wybranym okresie.
              Jeśli UZ ma już złożoną kartę (i nie jest rejected) — badge zamiast inputu. */}
          {secondaryContracts.map((sc) => {
            const company = sc.company_short_code ?? sc.company_name;
            const inactive = !sc.is_active_in_period;
            const periodNote = inactive
              ? sc.effective_to
                ? `Umowa: ${formatDate(sc.effective_from)} – ${formatDate(sc.effective_to)} (nie obejmuje wybranego okresu)`
                : `Umowa rozpoczyna się ${formatDate(sc.effective_from)} — wybierz późniejszy okres`
              : null;
            const entries = existingTimesheets.filter((e) => e.secondary_contract === sc.id);
            const occupying = entries.filter((e) => e.status !== "rejected");
            const alreadySubmitted = occupying.length > 0;
            const inFlight = entries.some((e) => e.status === "withdrawal_requested");
            const canResubmit =
              !anyInFlight && alreadySubmitted && occupying.every((e) => e.status === "approved");
            return (
              <div key={sc.id} className={inactive ? "opacity-60" : ""}>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Umowa zlecenie{company ? ` — ${company}` : ""}{" "}
                  {!alreadySubmitted && (
                    <span className="text-gray-400 text-xs font-normal">(opcjonalne)</span>
                  )}
                </label>
                {inactive ? (
                  <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-md px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400 italic">
                    {periodNote}
                  </div>
                ) : alreadySubmitted ? (
                  <>
                    <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <span>
                        Karta UZ za {month.toString().padStart(2, "0")}/{year} jest już złożona{" "}
                        {inFlight ? (
                          <Badge color="orange">wymiana w toku</Badge>
                        ) : (
                          <>
                            {occupying.some((e) => e.status === "approved") && <Badge color="green">zatwierdzona</Badge>}
                            {occupying.some((e) => e.status === "pending") && <Badge color="yellow">oczekuje</Badge>}
                          </>
                        )}
                        .
                      </span>
                      {inFlight && (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1.5">
                          Poprzednia karta oczekuje na akceptację wycofania przez dyrektora,
                          a nowa — na zatwierdzenie.
                        </p>
                      )}
                      {canResubmit && (
                        <label className="flex items-center gap-2 mt-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 dark:border-gray-600"
                            checked={resubmitSlot === sc.id}
                            disabled={resubmitSlot !== null && resubmitSlot !== sc.id}
                            onChange={(ev) => {
                              setResubmitSlot(ev.target.checked ? sc.id : null);
                              setSecondaryFiles((prev) => ({ ...prev, [sc.id]: null }));
                            }}
                          />
                          <span>Złóż ponownie — wymień zatwierdzoną kartę</span>
                        </label>
                      )}
                    </div>
                    {resubmitSlot === sc.id && (
                      <div className="mt-2">
                        <p className="text-xs text-amber-700 dark:text-amber-400 mb-1.5">
                          Obecna karta zostanie zgłoszona do wycofania. Dyrektor osobno
                          zaakceptuje wycofanie starej karty i osobno zatwierdzi nową.
                        </p>
                        <FileDropZone
                          files={secondaryFiles[sc.id] ? [secondaryFiles[sc.id]!] : []}
                          onFilesChange={(fs) =>
                            setSecondaryFiles((prev) => ({ ...prev, [sc.id]: fs[0] ?? null }))
                          }
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <FileDropZone
                    files={secondaryFiles[sc.id] ? [secondaryFiles[sc.id]!] : []}
                    onFilesChange={(fs) =>
                      setSecondaryFiles((prev) => ({ ...prev, [sc.id]: fs[0] ?? null }))
                    }
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                )}
              </div>
            );
          })}

          {resubmitSlot !== null && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 -mt-1">
              Możesz wymienić tylko jedną kartę na raz — np. przy UZ i UoP wybierz jedną z nich.
            </p>
          )}

          {secondaryContracts.some((sc) => sc.is_active_in_period) && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 -mt-1">
              UZ możesz dosłać później — pole nie jest wymagane.
            </p>
          )}

          {errors.submit && <p className="text-sm text-red-600">{errors.submit}</p>}

          <div className="flex gap-3">
            <Button loading={submitting} onClick={submit}>
              Złóż kartę
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="Usuń kartę godzin">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Czy na pewno chcesz usunąć tę kartę godzin? Tej operacji nie można cofnąć.
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              loading={deleteTimesheet.isPending}
              onClick={() => {
                if (!deleteId) return;
                deleteTimesheet.mutate(deleteId, { onSuccess: () => setDeleteId(null) });
              }}
            >
              Usuń
            </Button>
            <Button variant="secondary" onClick={() => setDeleteId(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      <TimesheetPdfModal open={pdfOpen} onClose={() => setPdfOpen(false)} />

      <Modal
        open={blankModalOpen}
        onClose={() => setBlankModalOpen(false)}
        title="Pobierz pustą kartę godzin"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Okres
            </label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={`${blankYear}-${blankMonth}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                setBlankYear(y);
                setBlankMonth(m);
              }}
            >
              {BLANK_PERIODS.map((p) => (
                <option key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button loading={blankDownloading} onClick={downloadBlankPdf}>
              <Download size={14} className="mr-1.5" /> Pobierz PDF
            </Button>
            <Button variant="secondary" onClick={() => setBlankModalOpen(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
